import React, { useState } from "react";
import { useLang } from "../contexts/LangContext";
import RequestServiceModal from "./RequestServiceModal";
import authenticateImg from "../assets/services/authenticate.png";
import serviceImg from "../assets/services/service.jpeg";
import polishImg from "../assets/services/polish.jpg";
import iceImg from "../assets/services/ice.webp";
import protectImg from "../assets/services/protect.jpeg";

const SERVICES = [
  { key: "authenticate", image: authenticateImg },
  { key: "service",      image: serviceImg      },
  { key: "polish",       image: polishImg       },
  { key: "ice",          image: iceImg          },
  { key: "protect",      image: protectImg      },
];

/**
 * Five concierge services rendered as image-top / text-bottom cards.
 *
 * Variants:
 *   - "landing"  full section header + generous padding, used on homepage.
 *   - "page"     bare grid + minimal top padding, used on the /atelier page
 *     where the entire section must fit in one viewport alongside the page
 *     header — no scrolling needed to see title + all five cards + REQUEST.
 *
 * Layout: 1 col mobile → 2 sm → 3 lg → 5 xl. Image height and text block are
 * fixed so every card is the same height and no service is visually dominant.
 */
export default function ServicesSection({ variant = "landing" }) {
  const { t } = useLang();
  const [modal, setModal] = useState({ open: false, service: null });
  const open = (k) => setModal({ open: true, service: k });
  const close = () => setModal({ open: false, service: null });

  const isPage = variant === "page";

  return (
    <section
      id="services"
      className={
        isPage
          ? "max-w-7xl w-full mx-auto px-6 lg:px-10 pb-6"
          : "max-w-7xl mx-auto px-6 lg:px-10 py-12 md:py-14"
      }
      data-testid="services-section"
      aria-labelledby={!isPage ? "services-heading" : undefined}
    >
      {!isPage && (
        <div className="text-center mb-10">
          <div className="overline text-gold mb-3">{t("services.kicker")}</div>
          <h2
            id="services-heading"
            className="font-serif text-3xl md:text-4xl mb-3"
            data-testid="services-title"
          >
            {t("services.title")}
          </h2>
          <p className="text-sm text-white/60 max-w-xl mx-auto">{t("services.subtitle")}</p>
        </div>
      )}

      <div
        className="grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
        data-testid="services-grid"
      >
        {SERVICES.map((s) => (
          <ServiceCard key={s.key} k={s.key} image={s.image} t={t} onRequest={open} />
        ))}
      </div>

      <RequestServiceModal open={modal.open} initialService={modal.service} onClose={close} />
    </section>
  );
}

/**
 * Split card: fixed-height image at the top (crops the photo tightly), then a
 * consistent text block underneath with title, description, and Request link.
 * Total card height sits at ~360px on desktop → fits comfortably inside a
 * 720/800px viewport once the page header (~120px) and site header (~80px)
 * are accounted for.
 */
function ServiceCard({ k, image, t, onRequest }) {
  return (
    <article
      data-testid={`service-card-${k}`}
      className="group glass rounded-lg overflow-hidden flex flex-col"
    >
      <div className="relative w-full h-32 md:h-36 xl:h-40 overflow-hidden">
        <img
          src={image}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />
      </div>
      <div className="flex flex-col flex-1 p-4">
        <h3
          className="font-serif text-lg xl:text-xl mb-1.5 leading-tight"
          data-testid={`service-card-${k}-title`}
        >
          {t(`services.${k}.title`)}
        </h3>
        <p className="text-[12.5px] text-white/75 leading-relaxed flex-1">
          {t(`services.${k}.desc`)}
        </p>
        <button
          type="button"
          onClick={() => onRequest?.(k)}
          data-testid={`service-request-${k}`}
          className="self-start mt-3 text-[11px] uppercase tracking-widest text-gold hover:text-gold-hover border-b border-gold/50 hover:border-gold pb-0.5 transition-colors"
        >
          Request →
        </button>
      </div>
    </article>
  );
}
